import React, { useEffect, useMemo, useState } from "react";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CustomDatePicker } from "../../components/custom-date-picker/custom-date-picker.component";
import { SammelrechnungenTable } from "./components/sammelrechnungen-table.component";
import { TotalsFooter } from "./components/totals-footer.component";
import { QrValidationErrorDialog } from "./components/qr-validation-error-dialog.component";
import { CustomDateRangePicker } from "../../components/custom-date-range-picker/custom-date-range-picker.component";
import { supabase } from "@/lib/supabase";
import { useCentralStore } from "../../store/Central";
import { showNotification } from "../../store/Central/selectors";
import { RecipientTypeEnum } from "./types";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import {
	SupabaseViewEnum,
	SupabaseShareEntityViewEnum,
} from "@/lib/supabase/supabaseTypes";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { Button } from "../../components/ui/button";
import { pageToIcon } from "../../layout/layout-components/app-bar/app-bar-components/app-bar-lists/page-to-icon";
import { usePatientStore } from "../../store/Patient";
import { useJobStore } from "../../store/Jobs";
import {
	SheetContentType,
	useSheetStore,
} from "../../store/Sheets/sheet.store";
import { useJobSheet } from "../../store/Sheets/sheet-hooks/useJobSheet";
import { useAccountingStore } from "../../store/Accounting";
import { useExportActions } from "../../hooks/actions/useExportActions";
import { getBankAccount } from "../../hooks/actions/get-bank-account/get-bank-account";
import { Logger } from "@/lib/logger/Logger";
import {
	dispatchPrint,
	isAutoPrintEnabled,
	getResolvedPrintMode,
} from "../../lib/utils/print-dispatch";
import { useDesktopsStore } from "../../store/Desktops";
import { Checkbox } from "../../components/ui/checkbox";
import { Printer } from "lucide-react";
import {
	calculateSammelrechnungRowsAndTotals,
	getMwStPercentageForSammelrechnung,
} from "../../lib/utils/calculate";
import { isInDateRange, hasConflict } from "./sammelrechnungen-table.utils";
import { useNavigate, useBeforeUnload } from "react-router-dom";
import { getGuarantorDisplayName } from "../../lib/utils/guarantor-utils";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { CompletedJobDocumentType } from "@/lib/supabase/supabaseTypes";
import {
	formatPaymentSlipData,
	validatePaymentSlipData,
} from "../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { validateQrCodeRendering } from "../../pdf-templates/pdf-template-components/qr-payment-slip/validate-qr-rendering";
import {
	getCurrencyFromRegion,
	hidePaymentSlip,
} from "../../lib/utils/region-utils";
import { FeatureGateOverlay } from "../../components/subscription/feature-gate-overlay.component";

interface RecipientGroup {
	recipientType: RecipientTypeEnum;
	recipientId: string;
	clientId: string;
	jobDocuments: CompletedJobDocumentType[];
}

export const MonthlyInvoicesPage = () => {
	const navigate = useNavigate();
	const {
		organizationId,
		organization,
		bankAccountsLookup,
		clientsLookup,
		guarantorLookup,
		practitionersLookup,
		getPractitioners,
		activatedRegions,
	} = useCentralStore((state) => ({
		organizationId: state.organization?.id,
		organization: state.organization,
		bankAccountsLookup: state.bankAccountsLookup,
		clientsLookup: state.clientsLookup,
		guarantorLookup: state.guarantorLookup,
		practitionersLookup: state.practitionersLookup,
		getPractitioners: state.getPractitioners,
		activatedRegions: state.activatedRegions,
	}));

	const { patientName, fetchPatientsByIds, patientsLookup } = usePatientStore(
		(state) => ({
			patientName: state.patientName,
			fetchPatientsByIds: state.fetchPatientsByIds,
			patientsLookup: state.patientsLookup,
		})
	);

	const { shareFile, unshareFile, acceptJobRequest, denyJobRequest } =
		useJobStore((state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			acceptJobRequest: state.acceptJobRequest,
			denyJobRequest: state.denyJobRequest,
		}));

	const { openJobSheet } = useJobSheet();
	const { openSheet } = useSheetStore((state) => ({
		openSheet: state.openSheet,
	}));

	const {
		createInvoiceMonthly,
		getExtendedJobDocuments,
		akontoLookup,
		fetchAkontoView,
	} = useAccountingStore((state) => ({
		createInvoiceMonthly: state.createInvoiceMonthly,
		getExtendedJobDocuments: state.getExtendedJobDocuments,
		akontoLookup: state.akontoLookup,
		fetchAkontoView: state.fetchAkontoView,
	}));

	const { exportSammelrechnung } = useExportActions();

	const [loading, setLoading] = useState<boolean>(false);
	const [bulkCreationProgress, setBulkCreationProgress] = useState<{
		isActive: boolean;
		current: number;
		total: number;
		currentRecipient?: string;
	}>({ isActive: false, current: 0, total: 0 });

	// Combined loading state for page leave protection
	const isAnyOperationRunning = bulkCreationProgress.isActive;
	const [allRecipientGroups, setAllRecipientGroups] = useState<
		RecipientGroup[]
	>([]);

	// Archived clients must not appear in monthly invoice creation. Derived so the
	// view updates as clientsLookup loads or changes (e.g. a client gets archived).
	const recipientGroups = useMemo(
		() =>
			allRecipientGroups.filter(
				(g) => !clientsLookup[g.clientId]?.is_inactive
			),
		[allRecipientGroups, clientsLookup]
	);

	// Selection state: recipientKey -> Set of selected job document IDs
	const [selectedJobDocumentIds, setSelectedJobDocumentIds] = useState<
		Record<string, Set<number>>
	>({});

	// Invoice date state: recipientKey -> Date for individual recipients
	const [invoiceDates, setInvoiceDates] = useState<Record<string, Date>>({});

	// Global invoice date for bulk creation (null = use individual dates)
	const [globalInvoiceDate, setGlobalInvoiceDate] = useState<Date | null>(
		null
	);

	// Akonto enabled state: recipientKey -> boolean
	const [akontoEnabled, setAkontoEnabled] = useState<Record<string, boolean>>(
		{}
	);

	// QR validation error dialog state
	const [qrErrorDialog, setQrErrorDialog] = useState<{
		open: boolean;
		validationError?: string;
		renderError?: string;
		qrData?: string;
	}>({ open: false });

	const [dateValue, setDateValue] = useState<SelectionRange>({
		// first day of current month
		start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		// today
		end: new Date(),
	});

	useEffect(() => {
		fetchData();
		if (organizationId) {
			fetchAkontoView();
		}
	}, [organizationId]);

	// Page leave protection - handles browser navigation (close tab, refresh, external links)
	useBeforeUnload(
		React.useCallback(
			(event) => {
				if (isAnyOperationRunning) {
					event.preventDefault();
					return "Rechnungsexport läuft noch, ein Abbruch wird zu Fehlern führen. Wirklich verlassen?";
				}
			},
			[isAnyOperationRunning]
		)
	);

	// In-app navigation protection - handles back button and programmatic navigation within SPA
	useEffect(() => {
		if (!isAnyOperationRunning) return;

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			return "Rechnungsexport läuft noch, ein Abbruch wird zu Fehlern führen. Wirklich verlassen?";
		};

		const handlePopState = (event: PopStateEvent) => {
			if (isAnyOperationRunning) {
				const shouldStay = window.confirm(
					"Ein Rechnungsexport läuft noch, ein Abbruch wird zu Fehlern führen. Wirklich verlassen?"
				);
				if (!shouldStay) {
					// Push the current state back to prevent navigation
					window.history.pushState(null, "", window.location.href);
				}
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		window.addEventListener("popstate", handlePopState);

		// Push a dummy state to catch back button
		window.history.pushState(null, "", window.location.href);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			window.removeEventListener("popstate", handlePopState);
		};
	}, [isAnyOperationRunning]);

	// Auto-select job documents in date range when date changes
	useEffect(() => {
		const newSelectedIds: Record<string, Set<number>> = {};
		const newInvoiceDates: Record<string, Date> = {};

		recipientGroups.forEach((group) => {
			const recipientKey = `${group.clientId}_${group.recipientId}`;
			const jobDocsInRange = group.jobDocuments.filter((job) =>
				isInDateRange(job, dateValue)
			);
			newSelectedIds[recipientKey] = new Set(
				jobDocsInRange.map((job) => job.id as number)
			);

			// Initialize invoice date for this recipient if not already set
			if (!invoiceDates[recipientKey]) {
				newInvoiceDates[recipientKey] = new Date();
			}
		});

		setSelectedJobDocumentIds(newSelectedIds);

		// Only update invoice dates for new recipients
		if (Object.keys(newInvoiceDates).length > 0) {
			setInvoiceDates((prev) => ({ ...prev, ...newInvoiceDates }));
		}
	}, [dateValue, recipientGroups]);

	// Fetch patient data when recipient groups change
	useEffect(() => {
		const allPatientIds = recipientGroups.flatMap((group) =>
			group.jobDocuments
				.filter((job) => job.job_patient_id)
				.map((job) => job.job_patient_id as string)
		);

		if (allPatientIds.length > 0) {
			fetchPatientsByIds(allPatientIds);
		}

		// Fetch practitioners for all clients
		const clientIds = [
			...new Set(recipientGroups.map((group) => group.clientId)),
		];
		clientIds.forEach((clientId) => {
			getPractitioners(clientId);
		});
	}, [recipientGroups, fetchPatientsByIds, getPractitioners]);

	const fetchData = async () => {
		if (!organizationId) {
			showNotification({
				message: "Fehler beim Laden der Daten: Keine Organisation",
				type: "error",
			});
			return;
		}

		setLoading(true);

		// We deliberately don't use .eq("organization_id", organizationId);
		// because it lead to performance issues. RLS already filters the data for the current organization.
		const { data, error } = await supabase
			.from(SupabaseViewEnum.COMPLETED_JOB_DOCUMENTS_VIEW)
			.select();

		if (error) {
			showNotification({
				message: "Fehler beim Laden der Daten",
				type: "error",
			});
			return;
		}

		const jobDocumentsPerRecipient = data.reduce(
			(acc: any, curr: any) => {
				let recipientId = null;
				let recipientType = null;
				let clientId = null;
				let key = null;

				// Create a composite key that includes both client_id and guarantor_id
				if (curr.guarantor_id && curr.client_id) {
					// Both client and guarantor exist - use composite key
					recipientId = curr.guarantor_id;
					recipientType = RecipientTypeEnum.GUARANTOR;
				} else if (curr.client_id) {
					// Only client exists
					recipientId = curr.client_id;
					recipientType = RecipientTypeEnum.CLIENT;
				} else {
					return acc;
				}

				if (!recipientId) {
					return acc;
				}
				clientId = curr.client_id;
				key = `${clientId}_${recipientId}`;
				if (!acc[key]) {
					acc[key] = {
						recipientType,
						recipientId,
						clientId,
						jobDocuments: [],
					};
				}
				acc[key].jobDocuments.push(curr);
				return acc;
			},
			{} as Record<string, RecipientGroup>
		);

		setAllRecipientGroups(Object.values(jobDocumentsPerRecipient));
		setLoading(false);
	};

	const handleSelectionChange = (
		recipientKey: string,
		selectedIds: Set<number>
	) => {
		setSelectedJobDocumentIds((prev) => ({
			...prev,
			[recipientKey]: selectedIds,
		}));
	};

	const handleInvoiceDateChange = (recipientKey: string, date: Date) => {
		setInvoiceDates((prev) => ({
			...prev,
			[recipientKey]: date,
		}));
	};

	const handleAkontoToggle = (recipientKey: string) => {
		setAkontoEnabled((prev) => ({
			...prev,
			[recipientKey]: !prev[recipientKey],
		}));
	};

	// Helper function to check if selected job documents have conflicting invoice settings
	const getSelectedJobsConflicts = (recipientKey: string): string[] => {
		const [clientId, recipientId] = recipientKey.split("_");
		const recipientGroup = recipientGroups.find(
			(g) => g.clientId === clientId && g.recipientId === recipientId
		);

		if (!recipientGroup) return [];

		const selectedIds = selectedJobDocumentIds[recipientKey] || new Set();
		const selectedJobs = recipientGroup.jobDocuments.filter((job) =>
			selectedIds.has(job.id as number)
		);

		if (selectedJobs.length === 0) return [];

		const conflicts: string[] = [];

		// Use centralized hasConflict function for all checks
		if (hasConflict(selectedJobs.map((job) => job.job_tax_rate))) {
			conflicts.push("MwSt.-Satz");
		}

		if (hasConflict(selectedJobs.map((job) => job.job_mwst_number))) {
			conflicts.push("MwSt.-Nr.");
		}

		if (hasConflict(selectedJobs.map((job) => job.job_is_mwst_eust))) {
			conflicts.push("MwSt.-Typ");
		}

		if (hasConflict(selectedJobs.map((job) => job.job_round_to))) {
			conflicts.push("Rundung");
		}

		if (
			hasConflict(
				selectedJobs.map((job) => job.job_currency_text),
				"CHF"
			)
		) {
			conflicts.push("Währung");
		}

		return conflicts;
	};

	const handleOpenPatientDetail = (patientId: string) => {
		const fetchedPatient = patientsLookup[patientId];
		openSheet(SheetContentType.PATIENT, {
			patient: fetchedPatient,
		});
	};

	const handleOpenJobDetail = async (jobId: number) => {
		const { data: job, error: jobError } = await supabase
			.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
			.select("*")
			.eq("id", jobId)
			.single();

		if (jobError) {
			Logger.error(jobError, {}, "Error fetching job");
			showNotification({
				message: "Auftrag konnte nicht gefunden werden",
				type: "error",
			});
			return;
		}

		openJobSheet({
			job: job,
			onShareFile: shareFile,
			onUnshareFile: unshareFile,
			onAcceptJobRequest: acceptJobRequest,
			onDenyJobRequest: denyJobRequest,
		});
	};

	const handleCreateInvoice = async (
		recipientKey: string,
		selectedJobDocIds: number[],
		isBulkOperation: boolean = false
	) => {
		if (!organization) {
			const error = "Keine Organisation gefunden";
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			return;
		}

		if (selectedJobDocIds.length === 0) {
			const error = "Keine Lieferscheine ausgewählt";
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			return;
		}

		// Check for conflicts in selected documents
		const conflicts = getSelectedJobsConflicts(recipientKey);
		if (conflicts.length > 0) {
			const conflictMessage =
				conflicts.length === 1
					? `Konflikt bei: ${conflicts[0]}`
					: `Konflikte bei: ${conflicts.join(", ")}`;
			const error = `Alle ausgewählten Lieferscheine müssen übereinstimmende Einstellungen haben. ${conflictMessage}`;
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			return;
		}

		if (!isBulkOperation) {
			setLoading(true);
		}

		// Find recipient info from recipientKey
		const [clientId, recipientId] = recipientKey.split("_");
		const recipientGroup = recipientGroups.find(
			(g) => g.clientId === clientId && g.recipientId === recipientId
		);

		if (!recipientGroup) {
			const error = "Empfänger nicht gefunden";
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			setLoading(false);
			return;
		}

		const recipient =
			recipientGroup.recipientType === RecipientTypeEnum.CLIENT
				? clientsLookup[recipientId]
				: guarantorLookup[recipientId];

		if (!recipient) {
			const error = "Empfänger nicht gefunden";
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			if (!isBulkOperation) {
				setLoading(false);
			}
			return;
		}

		const { bankAccount, error: bankAccountError } = getBankAccount({
			bankAccountsLookup: bankAccountsLookup,
			lab: organization,
			job: null,
			recipient: recipient,
		});

		if (!bankAccount) {
			const error = "Der Adressat hat kein Bankkonto";
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			setLoading(false);
			return;
		}

		const extendedJobDocuments =
			await getExtendedJobDocuments(selectedJobDocIds);

		if (!extendedJobDocuments) {
			const error = "Fehler beim Laden der Dokumente";
			if (isBulkOperation) {
				throw new Error(error);
			}
			if (!isBulkOperation) {
				setLoading(false);
			}
			return;
		}

		const { mwstPercentage, error } =
			getMwStPercentageForSammelrechnung(extendedJobDocuments);
		if (error) {
			const errorMsg =
				"Alle Aufträge müssen den gleichen MwSt.-Satz haben";
			if (isBulkOperation) {
				throw new Error(errorMsg);
			}
			showNotification({
				message: errorMsg,
				type: "error",
			});
			if (!isBulkOperation) {
				setLoading(false);
			}
			return;
		}

		const sammelrechnungCalculationResult =
			calculateSammelrechnungRowsAndTotals(
				extendedJobDocuments,
				organization,
				recipient,
				clientsLookup,
				mwstPercentage
			);

		const guarantorId =
			recipientGroup.recipientType === RecipientTypeEnum.GUARANTOR
				? recipientId
				: null;

		const organization_id = organization.id || "";

		// Determine which invoice date to use
		const invoiceDate =
			globalInvoiceDate ?? (invoiceDates[recipientKey] || new Date());

		// Determine akonto amount to use
		const akontoKey =
			recipientGroup.recipientType === RecipientTypeEnum.GUARANTOR
				? `guarantor:${recipientId}`
				: `client:${recipientId}`;
		const availableAkonto = akontoLookup[akontoKey] ?? 0;
		const isAkontoEnabled = akontoEnabled[recipientKey] ?? true;
		const akontoToUse = Math.min(
			availableAkonto,
			sammelrechnungCalculationResult.total.value
		);
		const akontoAmount = isAkontoEnabled ? akontoToUse : 0;

		const hasPayableAmount =
			sammelrechnungCalculationResult.total.value > 0;

		const client = clientsLookup[clientId];
		const currency = getCurrencyFromRegion(
			client,
			organization,
			activatedRegions
		);

		const { success: validationSuccess, error: validationError } =
			validatePaymentSlipData({
				bankAccount,
				recipient,
				amount: sammelrechnungCalculationResult.total.text,
				invoiceNumber: "999999",
				currency,
			});

		Logger.log(
			formatPaymentSlipData({
				bankAccount,
				recipient,
				amount: sammelrechnungCalculationResult.total.text,
				currency,
				invoiceNumber: "999999",
			})
		);

		if (hasPayableAmount && !validationSuccess) {
			const error = `QR-Rechnung kann nicht erstellt werden: ${validationError}`;
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			if (!isBulkOperation) {
				setLoading(false);
			}
			return;
		}

		const shouldHidePaymentSlip = hidePaymentSlip(
			client,
			organization,
			activatedRegions
		);

		if (hasPayableAmount && !shouldHidePaymentSlip) {
			const renderingValidationResult = await validateQrCodeRendering({
				bankAccount,
				recipient,
				amount: sammelrechnungCalculationResult.total.text,
				invoiceNumber: "999999",
				currency,
			});

			if (!renderingValidationResult.success) {
				setQrErrorDialog({
					open: true,
					validationError: renderingValidationResult.error,
					renderError: renderingValidationResult.renderError,
					qrData: renderingValidationResult.qrData,
				});

				if (isBulkOperation) {
					setBulkCreationProgress({
						isActive: false,
						current: 0,
						total: 0,
					});
					throw new Error("QR_VALIDATION_FAILED");
				}

				setLoading(false);
				return;
			}
		}

		const {
			success: createInvoiceSuccess,
			data: createInvoiceData,
			error: createInvoiceError,
		} = await createInvoiceMonthly({
			invoiceValue: sammelrechnungCalculationResult.total.value,
			bankAccountId: bankAccount.id,
			clientId,
			guarantorId,
			organizationId: organization_id,
			jobDocumentIds: selectedJobDocIds,
			invoiceDate: invoiceDate,
			akontoAmount: akontoAmount,
		});

		if (!createInvoiceSuccess || !createInvoiceData) {
			Logger.error(
				createInvoiceError,
				{},
				"Error creating monthly invoice",
				{
					createInvoiceError,
					createInvoiceData,
				},
				sammelrechnungCalculationResult,
				extendedJobDocuments
			);
			const error = "Fehler beim Erstellen der Rechnung";
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			if (!isBulkOperation) {
				setLoading(false);
			}
			return;
		}

		const { invoiceNumber, filePath, akontoUsed } = createInvoiceData;

		const { error: exportError, blob: exportBlob } =
			await exportSammelrechnung({
				recipient: recipient,
				invoiceNumber,
				filePath,
				extendedJobDocuments: extendedJobDocuments,
				invoiceDate: invoiceDate,
				akontoAmount: akontoUsed,
				noPaymentSlip: !hasPayableAmount,
			});

		if (exportError) {
			const error = "Fehler beim Exportieren der Rechnung";
			if (isBulkOperation) {
				throw new Error(error);
			}
			showNotification({
				message: error,
				type: "error",
			});
			if (!isBulkOperation) {
				setLoading(false);
			}
			return;
		}

		if (exportBlob && isAutoPrintEnabled("srg")) {
			dispatchPrint({
				documentType: "srg",
				documentData: exportBlob,
				fileName: `Sammelrechnung-${invoiceNumber}.pdf`,
			});
		}

		if (!isBulkOperation) {
			showNotification({
				message: "Sammelrechnung erstellt",
				type: "success",
				action: {
					label: "Fakturajournal öffnen",
					onClick: () =>
						navigate(
							`/debitoren/fakturajournal?recipient=${recipientId}`
						),
				},
			});
		}

		// Remove the invoiced job documents from the local state
		setAllRecipientGroups((prev) => {
			return prev
				.map((group) => {
					const groupKey = `${group.clientId}_${group.recipientId}`;
					if (groupKey === recipientKey) {
						// Filter out the invoiced job documents
						const remainingJobDocuments = group.jobDocuments.filter(
							(doc) =>
								!selectedJobDocIds.includes(doc.id as number)
						);
						return {
							...group,
							jobDocuments: remainingJobDocuments,
						};
					}
					return group;
				})
				.filter((group) => group.jobDocuments.length > 0); // Remove empty groups
		});

		// Clear selection for individual operations
		if (!isBulkOperation) {
			setSelectedJobDocumentIds((prev) => ({
				...prev,
				[recipientKey]: new Set<number>(),
			}));
			await fetchAkontoView();
			setLoading(false);
		}
	};

	const handleCreateAllInvoices = async () => {
		// Get all recipients with selected job documents
		const recipientsToProcess = Object.entries(selectedJobDocumentIds)
			.filter(([recipientKey, selectedIds]) => selectedIds.size > 0)
			.map(([recipientKey, selectedIds]) => {
				const [clientId, recipientId] = recipientKey.split("_");
				const recipientGroup = recipientGroups.find(
					(g) =>
						g.clientId === clientId && g.recipientId === recipientId
				);
				const recipientName =
					recipientGroup?.recipientType ===
					RecipientTypeEnum.GUARANTOR
						? getGuarantorDisplayName(
								guarantorLookup[recipientId],
								true
							) || `Garant ${recipientId}`
						: getClientDisplayName(clientsLookup[clientId], true) ||
							`Kunde ${clientId}`;

				return {
					recipientKey,
					selectedIds: Array.from(selectedIds),
					recipientName,
				};
			});

		if (recipientsToProcess.length === 0) {
			showNotification({
				message: "Keine Empfänger mit ausgewählten Dokumenten gefunden",
				type: "warning",
			});
			return;
		}

		// Initialize progress tracking
		setBulkCreationProgress({
			isActive: true,
			current: 0,
			total: recipientsToProcess.length,
		});

		// Fetch entire akonto view once before batch creation
		const { fetchAkontoView } = useAccountingStore.getState();
		await fetchAkontoView();

		let successCount = 0;
		let errorCount = 0;
		const errors: string[] = [];

		try {
			for (let i = 0; i < recipientsToProcess.length; i++) {
				const { recipientKey, selectedIds, recipientName } =
					recipientsToProcess[i];

				// Update progress
				setBulkCreationProgress({
					isActive: true,
					current: i + 1,
					total: recipientsToProcess.length,
					currentRecipient: recipientName,
				});

				try {
					await handleCreateInvoice(recipientKey, selectedIds, true);
					successCount++;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Unbekannter Fehler";

					if (errorMessage === "QR_VALIDATION_FAILED") {
						break;
					}

					errorCount++;
					errors.push(`${recipientName}: ${errorMessage}`);
					Logger.error(
						`Bulk creation failed for ${recipientKey}:` + error
					);
				}

				// Small delay to prevent overwhelming the server
				if (i < recipientsToProcess.length - 1) {
					await new Promise((resolve) => setTimeout(resolve, 500));
				}
			}
		} finally {
			// Reset progress
			setBulkCreationProgress({ isActive: false, current: 0, total: 0 });
			await fetchAkontoView();

			// Clear all selections after bulk operation
			if (successCount > 0) {
				setSelectedJobDocumentIds({});
			}
		}

		// Check if operation was interrupted by QR validation
		const wasInterrupted =
			successCount + errorCount < recipientsToProcess.length;

		// Show summary notification only if not interrupted by QR validation
		if (!wasInterrupted) {
			if (errorCount === 0) {
				showNotification({
					message: `${successCount} Sammelrechnungen erfolgreich erstellt`,
					type: "success",
					action: {
						label: "Fakturajournal öffnen",
						onClick: () => navigate("/debitoren/fakturajournal"),
					},
				});
			} else if (successCount > 0) {
				showNotification({
					message: `${successCount} von ${recipientsToProcess.length} Sammelrechnungen erstellt. ${errorCount} Fehler aufgetreten.`,
					type: "warning",
					description: errors.join("; "),
					action: {
						label: "Fakturajournal öffnen",
						onClick: () => navigate("/debitoren/fakturajournal"),
					},
				});
			} else {
				showNotification({
					message: `Alle ${recipientsToProcess.length} Rechnungen fehlgeschlagen`,
					type: "error",
					description: errors.join("; "),
				});
			}
		} else if (successCount > 0) {
			showNotification({
				message: `${successCount} Sammelrechnungen erstellt. Operation wegen QR-Validierungsfehler gestoppt.`,
				type: "warning",
				action: {
					label: "Fakturajournal öffnen",
					onClick: () => navigate("/debitoren/fakturajournal"),
				},
			});
		}
	};

	return (
		<FeatureGateOverlay>
			<div className="flex flex-col h-full px-4 py-6">
				<HeaderPortal>
					<PageTitle
						title={[{ name: "Sammelrechnungen" }]}
						icon={pageToIcon("monthly-invoices", {
							width: "16px",
							height: "16px",
							color: "rgb(55, 65, 81)",
						})}
					/>
				</HeaderPortal>

				{/* Page leave protection warning */}
				{isAnyOperationRunning && (
					<div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
						<div className="flex items-center gap-2">
							<div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
							<span className="text-orange-800 font-medium">
								Rechnungsexport läuft - Seite nicht verlassen!
							</span>
						</div>
						<div className="text-orange-700 text-sm mt-1">
							Der Export wird abgebrochen, wenn Sie die Seite
							verlassen, was zu Fehlern führen kann.
						</div>
					</div>
				)}
				<div className="flex flex-col gap-6 py-4 w-full flex-1 overflow-auto">
					<div className="flex flex-col gap-2.5 pb-5">
						<span className="text-sm font-semibold text-slate-900">
							Schnellauswahl
						</span>
						<CustomDateRangePicker
							dateVal={dateValue}
							handleDateChange={setDateValue}
						/>
					</div>

					{/* Global Invoice Date Picker and Bulk Export - Always visible */}
					<div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-4">
								<div className="flex flex-col gap-2">
									<label className="text-sm font-medium text-slate-900">
										Globales Rechnungsdatum:
									</label>
									<div className="flex items-center gap-2">
										<CustomDatePicker
											value={globalInvoiceDate}
											onDateChange={setGlobalInvoiceDate}
											className="w-40"
										/>
										{globalInvoiceDate && (
											<Button
												variant="outline"
												size="sm"
												onClick={() =>
													setGlobalInvoiceDate(null)
												}
												className="h-8"
											>
												Entfernen
											</Button>
										)}
									</div>
									<div className="text-sm text-slate-600 max-w-md">
										{globalInvoiceDate
											? "Das globale Datum wird für alle Rechnungen verwendet"
											: "Kein globales Datum - individuelle Rechnungsdaten werden verwendet"}
									</div>
								</div>
							</div>

							<div className="flex items-center gap-4">
								<AutoPrintToggle />
								<BulkCreationButton
									selectedJobDocumentIds={
										selectedJobDocumentIds
									}
									getSelectedJobsConflicts={
										getSelectedJobsConflicts
									}
									bulkCreationProgress={bulkCreationProgress}
									loading={loading}
									handleCreateAllInvoices={
										handleCreateAllInvoices
									}
								/>
							</div>
						</div>
					</div>

					<SammelrechnungenTable
						recipientGroups={recipientGroups}
						dateRange={dateValue}
						selectedJobDocumentIds={selectedJobDocumentIds}
						onSelectionChange={handleSelectionChange}
						onOpenPatientDetail={handleOpenPatientDetail}
						onOpenJobDetail={handleOpenJobDetail}
						clientsLookup={clientsLookup}
						guarantorLookup={guarantorLookup}
						practitionersLookup={practitionersLookup}
						patientsLookup={patientsLookup}
						patientName={patientName}
						onCreateInvoice={handleCreateInvoice}
						loading={loading}
						invoiceDates={invoiceDates}
						onInvoiceDateChange={handleInvoiceDateChange}
						getSelectedJobsConflicts={getSelectedJobsConflicts}
						globalInvoiceDate={globalInvoiceDate}
						akontoLookup={akontoLookup}
						akontoEnabled={akontoEnabled}
						onAkontoToggle={handleAkontoToggle}
					/>
				</div>

				<TotalsFooter
					recipientGroups={recipientGroups}
					selectedJobDocumentIds={selectedJobDocumentIds}
					akontoLookup={akontoLookup}
					akontoEnabled={akontoEnabled}
				/>

				<QrValidationErrorDialog
					open={qrErrorDialog.open}
					onOpenChange={(open) => setQrErrorDialog({ open })}
					validationError={qrErrorDialog.validationError}
					renderError={qrErrorDialog.renderError}
					qrData={qrErrorDialog.qrData}
				/>
			</div>
		</FeatureGateOverlay>
	);
};

const AutoPrintToggle: React.FC = () => {
	const isDesktop = getResolvedPrintMode() === "desktop";
	const { organization } = useCentralStore((s) => ({
		organization: s.organization,
	}));
	const { profile } = useCentralStore((s) => ({ profile: s.profile }));
	const { locations, upsertPrinterConfig, getAutoprint } = useDesktopsStore(
		(s) => ({
			locations: s.locations,
			upsertPrinterConfig: s.upsertPrinterConfig,
			getAutoprint: s.getAutoprint,
		})
	);

	const locationId =
		profile?.location_id ?? locations.find((l) => l.is_default)?.id ?? null;
	const checked = locationId ? getAutoprint(locationId, "srg") : false;

	if (!isDesktop || !locationId || !organization) return null;

	const handleToggle = (val: boolean) => {
		upsertPrinterConfig({
			organization_id: organization.id!,
			location_id: locationId!,
			config_key: "srg",
			auto_print: val,
		});
	};

	return (
		<label className="flex items-center gap-2 cursor-pointer">
			<Checkbox
				checked={checked}
				onCheckedChange={(val) => handleToggle(val as boolean)}
				aria-label="Auto-Druck Sammelrechnung"
				data-testid="auto-print-srg-toggle"
			/>
			<Printer className="w-4 h-4 text-slate-500" />
			<span className="text-sm text-slate-700">Auto-Druck</span>
		</label>
	);
};

const BulkCreationButton = ({
	selectedJobDocumentIds,
	getSelectedJobsConflicts,
	bulkCreationProgress,
	loading,
	handleCreateAllInvoices,
}: {
	selectedJobDocumentIds: Record<string, Set<number>>;
	getSelectedJobsConflicts: (recipientKey: string) => string[];
	bulkCreationProgress: {
		isActive: boolean;
		current: number;
		total: number;
		currentRecipient?: string;
	};
	loading: boolean;
	handleCreateAllInvoices: () => void;
}) => {
	const recipientsWithSelections = Object.keys(selectedJobDocumentIds).filter(
		(recipientKey) => selectedJobDocumentIds[recipientKey].size > 0
	);

	const recipientsWithIssues = recipientsWithSelections.filter(
		(recipientKey) => {
			const selectedIds = selectedJobDocumentIds[recipientKey];
			const conflicts = getSelectedJobsConflicts(recipientKey);
			return selectedIds.size === 0 || conflicts.length > 0;
		}
	);

	const hasIssues = recipientsWithIssues.length > 0;
	const recipientCount = recipientsWithSelections.length;

	if (recipientCount === 0) {
		return (
			<Button
				disabled={true}
				className="bg-slate-300 text-slate-500 cursor-not-allowed"
				size="sm"
			>
				Alle Rechnungen erstellen (0)
			</Button>
		);
	}

	if (hasIssues) {
		return (
			<div className="flex flex-col items-end gap-2">
				<Button
					disabled={true}
					className="bg-slate-300 text-slate-500 cursor-not-allowed"
					size="sm"
					title="Einige Empfänger haben widersprüchliche Einstellungen oder keine Auswahl"
				>
					Alle Rechnungen erstellen ({recipientCount})
				</Button>
				<div className="text-xs text-orange-600">
					{recipientsWithIssues.length} Empfänger mit Konflikten
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col items-end gap-2">
			<Button
				onClick={handleCreateAllInvoices}
				disabled={loading || bulkCreationProgress.isActive}
				className="bg-slate-600 hover:bg-slate-700 text-white"
				size="sm"
			>
				{bulkCreationProgress.isActive ? (
					<span className="flex items-center gap-2">
						<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						Erstelle {bulkCreationProgress.current} von{" "}
						{bulkCreationProgress.total}
					</span>
				) : (
					`Alle Rechnungen erstellen (${recipientCount})`
				)}
			</Button>
			{bulkCreationProgress.isActive &&
				bulkCreationProgress.currentRecipient && (
					<div className="text-xs text-slate-700">
						Bearbeite: {bulkCreationProgress.currentRecipient}
					</div>
				)}
		</div>
	);
};
